import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaGuitar,
  FaShoppingBag,
  FaInfoCircle,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBars,
  FaTimes,
  FaWhatsapp
} from "react-icons/fa";

// Logolar için import
import desktopLogo from "../assets/logo.png";
import mobileLogo from "../assets/mobillogopng.png";

export default function Navbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const mobileMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Scroll takibi - navbar'ın rengini değiştirmek için
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = "+905308648486";
    const message = "Merhaba, Neo Guitar Store'dan bilgi almak istiyorum.";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const menuItems = [
    { path: "/", label: "Anasayfa", icon: FaHome },
    { path: "/gitarlar", label: "Gitarlar", icon: FaGuitar },
    { path: "/siparis", label: "Sipariş & Takas", icon: FaShoppingBag },
    { path: "/hakkimizda", label: "Hakkımızda", icon: FaInfoCircle },
  ];

  const contactInfo = [
    { 
      icon: FaEnvelope, 
      text: "info@neoguitarstore.com",
      link: "mailto:info@neoguitarstore.com",
      type: "email"
    },
    { 
      icon: FaPhoneAlt, 
      text: "+90 530 864 84 86",
      link: "tel:+905308648486",
      type: "phone"
    },
    { 
      icon: FaMapMarkerAlt, 
      text: "İstanbul, Üsküdar",
      link: "https://maps.app.goo.gl/KG6z2CFfaoz7scSZ9?g_st=aw",
      type: "map"
    },
  ];

  const handleContactClick = (item) => {
    if (item.link) {
      window.open(item.link, '_blank');
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    }
  };

  return (
    <div className="relative">
      {/* ANA NAVBAR - SABİT */}
      <nav 
        className={`w-full fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 shadow-xl' 
            : 'bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 shadow-lg'
        } border-b border-gray-700`}
      >
        <div className="max-w-10xl mx-auto flex justify-between items-center px-4 sm:px-6 lg:px-24 py-2">
          <div className="flex items-center gap-4">
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
              aria-label="Menü"
            >
              {mobileMenuOpen ? (
                <FaTimes className="text-white text-xl" />
              ) : (
                <FaBars className="text-white text-xl" />
              )}
            </button>

            <Link to="/" className="flex items-center select-none z-10">
              {/* Masaüstü Logo */}
              <img
                src={desktopLogo}
                alt="Neo Guitar Store Logo"
                className="hidden md:block h-12 lg:h-12 w-auto object-contain"
              />
              
              {/* Mobil Logo */}
              <img
                src={mobileLogo}
                alt="Neo Guitar Store Logo"
                className="md:hidden h-14 sm:h-16 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Masaüstü Menü */}
          <div className="hidden lg:flex items-center space-x-1 absolute left-1/2 transform -translate-x-1/2 bg-gray-800 rounded-full px-2 py-1 shadow-lg border border-gray-600">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1 px-4 py-2 rounded-full transition-all font-semibold ${
                  isActive(item.path)
                    ? "bg-gray-700 text-white shadow-md"
                    : "hover:bg-gray-800 text-gray-200 hover:text-white"
                }`}
              >
                <item.icon className="text-sm" />
                <span className="text-sm">{item.label}</span>
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* İletişim Bilgileri - Masaüstü */}
            <div className="hidden md:flex items-center gap-4">
              {contactInfo.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleContactClick(item)}
                  className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors cursor-pointer group"
                  title={item.type === 'email' ? 'E-posta gönder' : 
                         item.type === 'phone' ? 'Ara' : 'Haritada göster'}
                >
                  <item.icon className="text-xs text-gray-400 group-hover:text-white transition-colors" />
                  <span className="text-xs group-hover:text-white transition-colors">
                    {item.text}
                  </span>
                </button>
              ))}
            </div>

            {/* WhatsApp Butonu */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleWhatsAppClick}
              aria-label="WhatsApp ile iletişim"
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-full transition-all duration-300"
            >
              <FaWhatsapp className="text-lg" />
              <span className="hidden sm:inline text-sm">WhatsApp</span>
            </motion.button>
          </div>
        </div>

        {/* Mobil Menü */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              initial={{ opacity: 0, x: -300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -300 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden fixed top-16 left-0 w-80 h-[calc(100vh-4rem)] bg-gray-900 shadow-2xl z-40 border-r border-gray-700 overflow-y-auto"
            >
              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-4">Menü</h3>

                <div className="space-y-2">
                  {menuItems.map((item) => (
                    <motion.div
                      key={item.path}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                          isActive(item.path)
                            ? "bg-gray-700 text-white"
                            : "hover:bg-gray-700 text-gray-200 hover:text-white"
                        }`}
                      >
                        <item.icon className="text-gray-300" />
                        <span>{item.label}</span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    handleWhatsAppClick();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center gap-2 p-3 mt-4 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-all"
                >
                  <FaWhatsapp className="text-lg" />
                  <span>WhatsApp ile İletişim</span>
                </motion.button>

                <div className="mt-6 pt-4 border-t border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-400 mb-3">İletişim</h4>
                  <div className="space-y-3">
                    {contactInfo.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleContactClick(item)}
                        className="w-full flex items-center gap-3 p-2 text-gray-300 hover:text-white transition-colors cursor-pointer text-left"
                        title={item.type === 'email' ? 'E-posta gönder' : 
                               item.type === 'phone' ? 'Ara' : 'Haritada göster'}
                      >
                        <item.icon className="text-gray-400" />
                        <span className="text-sm">{item.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Navbar yüksekliği kadar boşluk */}
      <div className="h-16"></div>
    </div>
  );
}